import React from "react";
import { Button, Tooltip } from "@material-ui/core";
import { css } from "@emotion/css";
import { useHistory } from "react-router-dom";
import { useKeepView } from "utils/useViewState";
import { CataloguePath } from "routes/createContent/paths";
import RolloutStatusChip from "routes/healthForms/components/RolloutStatusChip";
import { deployAttribution, groupLabel } from "routes/healthForms/rolloutText";
import {
  CatalogueGap,
  CatalogueGroupRollout,
  CatalogueKind,
  CatalogueRollout,
  ENV_GROUPS,
  EnvGroup,
} from "routes/createContent/api/catalogueDeployments";

const groupTooltip = (group: EnvGroup, entry: CatalogueGroupRollout) => {
  if (entry.status === "inactive") return `Not deployed to ${groupLabel(group)}`;
  const attribution = deployAttribution(entry);
  if (entry.status === "retiring") return `Deleted ${attribution} — waiting for environments to converge`;
  if (entry.status === "retired") return `Deleted from every ${groupLabel(group)} environment — ${attribution}`;
  if (entry.status === "activating") return `Deployed ${attribution} — waiting for environments to converge`;
  return entry.has_undeployed_changes
    ? `${groupLabel(group)} is serving the previously deployed state, deployed ${attribution}`
    : `Live on every ${groupLabel(group)} environment — deployed ${attribution}`;
};

const servingStaleState = (entry: CatalogueGroupRollout | undefined) =>
  entry?.status === "active" && entry.has_undeployed_changes;

const needsDeploy = (entry: CatalogueGroupRollout) =>
  entry.status === "inactive" || entry.status === "retired" || servingStaleState(entry);

const changesArea = (kind: CatalogueKind) => {
  switch (kind) {
    case "exercise":
      return CataloguePath.Exercises_Changes;
    case "lesson":
      return CataloguePath.Lessons_Changes;
    case "template":
      return CataloguePath.Templates_Changes;
  }
};

const changesPath = (kind: CatalogueKind, name: string, group: string) =>
  changesArea(kind).replace(":name", encodeURIComponent(name)).replace(":group", group);

export const CatalogueDeploymentRow: React.VFC<{
  kind: CatalogueKind;
  name: string;
  rollout: CatalogueRollout | undefined;
  compact?: boolean;
  unsavedChanges?: boolean;
  gaps?: CatalogueGap[];
}> = ({ kind, name, rollout, compact = false, unsavedChanges = false, gaps = [] }) => {
  const history = useHistory();
  const keep = useKeepView([]);

  if (!rollout) {
    return null;
  }

  const prod = rollout.prod;
  const canDeploy = !compact && prod !== undefined && needsDeploy(prod);
  const deployBlocker = unsavedChanges
    ? "Save first — the review shows the saved state"
    : gaps.length > 0
      ? "Fill the gaps listed below first"
      : null;

  return (
    <div className={compact ? `${styles.row} ${styles.compactRow}` : styles.row}>
      {ENV_GROUPS.map(group => {
        const entry = rollout[group];
        if (!entry) return null;
        const stale = servingStaleState(entry);
        return (
          <button
            key={group}
            type="button"
            className={styles.chipButton}
            onClick={event => {
              event.stopPropagation();
              history.push(keep(changesPath(kind, name, group)));
            }}
          >
            <RolloutStatusChip
              status={entry.status}
              label={groupLabel(group)}
              tooltip={stale ? `${groupTooltip(group, entry)} — click to review` : groupTooltip(group, entry)}
              stale={stale}
            />
          </button>
        );
      })}
      {canDeploy && (
        <span className={styles.action} onClick={event => event.stopPropagation()}>
          <Tooltip title={deployBlocker ?? "Review the changes, then deploy"}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={deployBlocker !== null}
                onClick={() => history.push(keep(changesPath(kind, name, "prod")))}
              >
                Deploy to prod…
              </Button>
            </span>
          </Tooltip>
        </span>
      )}
    </div>
  );
};

const styles = {
  row: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    width: 100%;
  `,
  compactRow: css`
    flex-wrap: nowrap;
  `,
  action: css`
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  `,
  chipButton: css`
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font: inherit;
  `,
};
